import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Receipt, Download, RefreshCw, AlertTriangle, Info, Calendar,
  TrendingUp, TrendingDown, Loader2, FileSpreadsheet, Calculator,
  HelpCircle, ChevronDown, ChevronUp,
} from 'lucide-react';
import { Spinner, InfoTip } from '../components/ui';
import { getCostBasisTaxReport, backfillSwapAmounts } from '../services/api';

const METHODS = [
  {
    id: 'FIFO', label: 'FIFO',
    tagline: 'First In, First Out — IRS default for crypto',
    longDescription: 'When you sell, the oldest tax lots are sold first. This is the IRS default and what most exchanges report by default on their tax forms.',
  },
  {
    id: 'LIFO', label: 'LIFO',
    tagline: 'Last In, First Out — most recent lots sold first',
    longDescription: 'When you sell, the most recently bought lots are sold first. Useful when newer lots have similar cost basis to current price (smaller realized gains).',
  },
  {
    id: 'HIFO', label: 'HIFO',
    tagline: 'Highest In, First Out — minimizes gains',
    longDescription: 'When you sell, the lots with the highest cost basis are sold first. This typically minimizes your taxable gains (or maximizes losses), giving you the lowest tax bill in most years. Allowed by the IRS as specific identification.',
  },
];

function Card({ children, className = '' }) {
  return <div className={`bg-dark-800 rounded-2xl p-6 border border-dark-700/50 ${className}`}>{children}</div>;
}
function fmtUsd(v, dp = 2) {
  if (v == null || !Number.isFinite(v)) return '—';
  const sign = v < 0 ? '-' : '';
  return `${sign}$${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
}
function pnlClass(v) {
  if (v == null || !Number.isFinite(v) || v === 0) return 'text-dark-400';
  return v > 0 ? 'text-green-400' : 'text-red-400';
}

function downloadCsv(filename, rows) {
  if (!rows || !rows.length) return;
  const headers = Object.keys(rows[0]);
  const lines = [headers.join(',')];
  for (const r of rows) {
    lines.push(headers.map(h => {
      const v = r[h];
      if (v == null) return '';
      const s = String(v);
      return /[,"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(','));
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

export default function TaxReportPage() {
  const currentYear = new Date().getUTCFullYear();
  const [method, setMethod] = useState('FIFO');
  const [year, setYear] = useState(currentYear);
  const [report, setReport] = useState(null);
  const [methodCompare, setMethodCompare] = useState({ FIFO: null, LIFO: null, HIFO: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedLot, setExpandedLot] = useState(null);
  const [showExplainer, setShowExplainer] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);

  // Best method = lowest total P&L (most favorable tax outcome). HIFO is usually
  // the winner but not always — when all lots have similar cost basis (e.g. a
  // grid bot trading in a tight range), the methods can tie or even invert.
  const bestMethod = useMemo(() => {
    const entries = Object.entries(methodCompare).filter(([, v]) => v && Number.isFinite(v.totalPnL));
    if (entries.length === 0) return null;
    entries.sort((a, b) => a[1].totalPnL - b[1].totalPnL);
    return entries[0][0];
  }, [methodCompare]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      // Fetch the active method (full data) + the other two for the comparison cards.
      const [active, others] = await Promise.all([
        getCostBasisTaxReport({ method, year }),
        Promise.all(METHODS.filter(m => m.id !== method).map(m =>
          getCostBasisTaxReport({ method: m.id, year }).then(r => ({ id: m.id, r })).catch(() => null)
        )),
      ]);
      setReport(active);
      const map = { [method]: { totalPnL: active.totalPnL, shortTermPnL: active.shortTermPnL, longTermPnL: active.longTermPnL } };
      for (const o of others) if (o) map[o.id] = { totalPnL: o.r.totalPnL, shortTermPnL: o.r.shortTermPnL, longTermPnL: o.r.longTermPnL };
      setMethodCompare(map);
    } catch (e) {
      setError(e?.message || 'Failed to load tax report');
    } finally {
      setLoading(false);
    }
  }, [method, year]);

  useEffect(() => { load(); }, [load]);

  // Heal historical DEX swap records whose `amountOut` was never populated
  // (only relevant for swaps executed before the 2026-06-01 fix). Calls the
  // backend backfill endpoint, which parses each tx receipt to extract the
  // actual received amount. Refreshes the tax report once done so the user
  // sees the new line items appear without a manual reload.
  const handleBackfillSwaps = useCallback(async () => {
    setBackfilling(true);
    setBackfillResult(null);
    try {
      const res = await backfillSwapAmounts();
      setBackfillResult(res);
      if (res?.patched > 0) {
        await load();
      }
    } catch (err) {
      setBackfillResult({ error: err?.message || 'Backfill failed' });
    } finally {
      setBackfilling(false);
    }
  }, [load]);

  const years = useMemo(() => {
    const arr = ['all'];
    for (let y = currentYear; y >= currentYear - 4; y--) arr.push(y);
    return arr;
  }, [currentYear]);

  const lotsSorted = useMemo(() => {
    if (!report?.lots) return [];
    return [...report.lots].sort((a, b) => b.sold.localeCompare(a.sold));
  }, [report]);

  const handleExportCsv = () => {
    if (!report?.lots?.length) return;
    const rows = report.lots.map(l => ({
      Asset: l.asset,
      Qty: l.qty,
      Acquired: l.acquired,
      Sold: l.sold,
      CostBasis: l.costBasis,
      Proceeds: l.proceeds,
      GainLoss: l.gainLoss,
      Term: l.term,
      HoldingDays: l.holdingDays,
      Source: l.source,
    }));
    downloadCsv(`haythix-tax-${method}-${year}.csv`, rows);
  };

  return (
    <div className="min-h-screen bg-dark-900 text-dark-100 p-6">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Receipt className="w-8 h-8 text-blue-400" />
              Tax Report
            </h1>
            <p className="text-dark-400 mt-1">
              Realized gains/losses from your actual fills, FIFO/LIFO/HIFO matched. 8949-ready line items.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={handleBackfillSwaps} disabled={backfilling || loading}
              title="Re-scan on-chain receipts for any DEX swap missing an amount. Use this once after first DEX swap activity if Tax Report looks incomplete."
              className="px-4 py-2 bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/40 text-amber-300 rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${backfilling ? 'animate-spin' : ''}`} />
              {backfilling ? 'Backfilling…' : 'Backfill DEX swaps'}
            </button>
            <button
              onClick={load} disabled={loading}
              className="px-4 py-2 bg-dark-800 hover:bg-dark-700 border border-dark-700/50 rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
            <button
              onClick={handleExportCsv} disabled={!report?.lots?.length}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="w-4 h-4" /> Export CSV
            </button>
          </div>
        </div>

        {/* Backfill result banner */}
        {backfillResult && (
          <div className={`rounded-lg px-4 py-3 text-sm border ${
            backfillResult.error
              ? 'bg-red-500/10 border-red-500/40 text-red-300'
              : backfillResult.patched > 0
                ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-300'
                : 'bg-dark-800 border-dark-700/50 text-dark-300'
          }`}>
            {backfillResult.error ? (
              <>Backfill failed: {backfillResult.error}</>
            ) : backfillResult.patched > 0 ? (
              <>
                Patched <strong>{backfillResult.patched}</strong> swap{backfillResult.patched === 1 ? '' : 's'} —
                scanned {backfillResult.scanned}, skipped {backfillResult.skipped}, errors {backfillResult.errors}. Report refreshed.
              </>
            ) : (
              <>
                No swaps needed backfilling — scanned {backfillResult.scanned}, all had amounts already. {backfillResult.errors > 0 ? `${backfillResult.errors} error${backfillResult.errors === 1 ? '' : 's'}.` : ''}
              </>
            )}
          </div>
        )}

        {/* Method + Year controls */}
        <Card>
          <div className="flex flex-wrap items-center gap-6">
            <div>
              <div className="text-xs text-dark-400 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                Cost-basis method
                <InfoTip side="bottom">Same trades, different number depending on which lot the engine matches against. FIFO = IRS default. HIFO = typically lowest tax (allowed via specific identification). LIFO = rarely the right choice for crypto.</InfoTip>
              </div>
              <div className="inline-flex rounded-lg bg-dark-900 p-1 border border-dark-700/50">
                {METHODS.map(m => (
                  <button
                    key={m.id}
                    onClick={() => setMethod(m.id)}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      method === m.id ? 'bg-blue-600 text-white' : 'text-dark-300 hover:text-dark-100'
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
              <div className="text-xs text-dark-400 mt-2 max-w-md">
                {METHODS.find(m => m.id === method)?.tagline}
              </div>
            </div>
            <div>
              <div className="text-xs text-dark-400 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" /> Tax year
                <InfoTip side="bottom">Pick the calendar year you're filing for. During filing season (Jan-Apr) you almost always want LAST year (e.g. filing in 2026 → pick 2025). "All years" is for lifetime view, not for actual filing.</InfoTip>
              </div>
              <select
                value={year}
                onChange={e => setYear(e.target.value === 'all' ? null : Number(e.target.value))}
                className="bg-dark-900 border border-dark-700/50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
              >
                {years.map(y => <option key={y} value={y}>{y === 'all' ? 'All years' : y}</option>)}
              </select>
            </div>
          </div>
        </Card>

        {/* Disclaimer */}
        <div className="bg-amber-900/20 border border-amber-700/30 rounded-lg p-3 flex items-start gap-3">
          <Info className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-amber-200/90 leading-relaxed">
            Informational only. Not tax advice. Numbers are computed from your fills on this platform —
            external wallets/exchanges that you haven't connected won't be included. Talk to a tax professional before filing.
          </div>
        </div>

        {/* Educational explainer — collapsible. Answers two recurring questions:
            1) What do FIFO / LIFO / HIFO actually mean?
            2) Why doesn't this match my Grid Bot / Smart Trade dashboard P&L? */}
        <Card className="!p-0">
          <button
            onClick={() => setShowExplainer(v => !v)}
            className="w-full flex items-center justify-between px-5 py-3 hover:bg-dark-700/30 transition rounded-2xl"
          >
            <div className="flex items-center gap-2">
              <HelpCircle className="w-4 h-4 text-blue-400" />
              <span className="text-sm font-medium text-white">Understanding FIFO / LIFO / HIFO — and why this can differ from your bot dashboards</span>
            </div>
            {showExplainer ? <ChevronUp className="w-4 h-4 text-dark-400" /> : <ChevronDown className="w-4 h-4 text-dark-400" />}
          </button>
          {showExplainer && (
            <div className="px-5 pb-5 pt-1 space-y-4 text-sm text-dark-300 border-t border-dark-700/30">
              <div>
                <p className="text-white font-medium mb-1">What are tax lots?</p>
                <p className="text-dark-400 leading-relaxed">
                  Every time you BUY an asset, you create a "tax lot" — a record of what you paid. When you sell, the IRS needs to know <em>which lot</em> you sold from to compute the gain or loss. You can choose the matching method below — same trades, different number.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {METHODS.map(m => (
                  <div key={m.id} className="bg-dark-900/60 rounded-lg p-3 border border-dark-700/40">
                    <div className="text-xs font-bold text-blue-400 mb-1">{m.label}</div>
                    <div className="text-xs text-dark-400 leading-relaxed">{m.longDescription}</div>
                  </div>
                ))}
              </div>
              <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg p-3">
                <p className="text-blue-300 font-medium mb-1.5 text-sm">Why doesn't this match my Grid Bot / Smart Trade dashboard?</p>
                <p className="text-dark-300 text-xs leading-relaxed">
                  Your bots show <strong>per-strategy</strong> P&amp;L — Grid Bot pairs each sell at level X+1 with its buy at level X, in isolation. That's the right view for "how is my bot doing?"
                </p>
                <p className="text-dark-300 text-xs leading-relaxed mt-2">
                  The IRS, however, doesn't care about strategies. It treats <strong>all your ETH as one pool</strong>, regardless of whether the buy came from Grid Bot, Smart Trade, DCA Safety, or a manual CEX trade. When you sell, the matching method below decides which lot from that pool to draw down. A Grid Bot sell at $2,105 can legitimately match against a Smart Trade buy at $2,200 from weeks ago — that's a tax loss even though your bot considers that pair out of scope.
                </p>
                <p className="text-dark-300 text-xs leading-relaxed mt-2">
                  <strong>Both numbers are right.</strong> The bot dashboard is your operational view; this report is what TurboTax, Koinly, or Coinbase would produce from the same fills — and what the IRS expects.
                </p>
              </div>
              {bestMethod && (
                <div className="bg-emerald-900/20 border border-emerald-700/30 rounded-lg p-3">
                  <p className="text-emerald-300 text-xs">
                    For <strong>{report?.year === 'all' ? 'your entire history' : `tax year ${report?.year}`}</strong>, <strong>{bestMethod}</strong> currently produces the most favorable (lowest) realized P&amp;L. This means it's likely your best choice for minimizing tax owed — but only specific identification (HIFO) is universally allowed; check with a tax pro before switching default methods between years.
                  </p>
                </div>
              )}
            </div>
          )}
        </Card>

        {loading && !report && (
          <div className="flex items-center justify-center py-16">
            <Spinner /> <span className="ml-3 text-dark-400">Matching lots…</span>
          </div>
        )}

        {error && (
          <Card>
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5" />
              <div className="text-sm text-red-300">{error}</div>
            </div>
          </Card>
        )}

        {report && !error && (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <div className="text-xs text-dark-400 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  Short-term P&amp;L
                  <InfoTip side="bottom">Realized gains/losses on lots held LESS THAN 1 YEAR. Taxed as ORDINARY INCOME (22-37% federal for most filers + state). Short-term losses are the most valuable to harvest because they offset higher-tax income first.</InfoTip>
                </div>
                <div className={`text-3xl font-bold ${pnlClass(report.shortTermPnL)}`}>
                  {fmtUsd(report.shortTermPnL)}
                </div>
                <div className="text-xs text-dark-400 mt-2">Held &lt; 1 year — taxed as ordinary income</div>
              </Card>
              <Card>
                <div className="text-xs text-dark-400 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  Long-term P&amp;L
                  <InfoTip side="bottom">Realized gains/losses on lots held 1+ YEAR. Qualifies for preferential capital gains rates (0% / 15% / 20% federal depending on income bracket). The good bucket — if you can hold for over a year, you save significant tax.</InfoTip>
                </div>
                <div className={`text-3xl font-bold ${pnlClass(report.longTermPnL)}`}>
                  {fmtUsd(report.longTermPnL)}
                </div>
                <div className="text-xs text-dark-400 mt-2">Held ≥ 1 year — preferential capital gains rate</div>
              </Card>
              <Card>
                <div className="text-xs text-dark-400 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  Total realized P&amp;L
                  <InfoTip side="bottom">Sum of short-term + long-term. This is the headline number that flows to IRS Form 8949 → Schedule D. The lot count below = how many matched (buy, sell) pairs produced that total.</InfoTip>
                </div>
                <div className={`text-3xl font-bold ${pnlClass(report.totalPnL)}`}>
                  {fmtUsd(report.totalPnL)}
                </div>
                <div className="text-xs text-dark-400 mt-2">
                  {report.lotCount} matched lot{report.lotCount === 1 ? '' : 's'} for {report.year === 'all' ? 'all years' : report.year}
                </div>
              </Card>
            </div>

            {/* Method comparison */}
            <Card>
              <div className="flex items-center gap-2 mb-4">
                <Calculator className="w-4 h-4 text-blue-400" />
                <div className="text-sm font-semibold">Method comparison</div>
                <div className="text-xs text-dark-400">— same trades, different lot-matching</div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {METHODS.map(m => {
                  const c = methodCompare[m.id];
                  const active = m.id === method;
                  const isBest = bestMethod && m.id === bestMethod && Object.keys(methodCompare).filter(k => methodCompare[k]).length > 1;
                  return (
                    <button
                      key={m.id}
                      onClick={() => setMethod(m.id)}
                      className={`relative text-left rounded-lg p-3 border transition-colors ${
                        active
                          ? 'bg-blue-900/30 border-blue-500/50'
                          : 'bg-dark-900 border-dark-700/50 hover:border-dark-600'
                      }`}
                    >
                      {isBest && (
                        <span className="absolute top-1.5 right-1.5 text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                          LOWEST TAX
                        </span>
                      )}
                      <div className="text-xs text-dark-400 uppercase tracking-wide">{m.label}</div>
                      <div className={`text-lg font-bold mt-1 ${pnlClass(c?.totalPnL)}`}>
                        {c ? fmtUsd(c.totalPnL) : '—'}
                      </div>
                      <div className="text-[11px] text-dark-500 mt-1">
                        ST {fmtUsd(c?.shortTermPnL)} · LT {fmtUsd(c?.longTermPnL)}
                      </div>
                    </button>
                  );
                })}
              </div>
            </Card>

            {/* Warnings if any */}
            {!report.lotCount && (
              <Card>
                <div className="text-center py-8">
                  <FileSpreadsheet className="w-10 h-10 text-dark-500 mx-auto mb-3" />
                  <div className="text-dark-300 font-medium">No closed lots {report.year !== 'all' && `in ${report.year}`} yet</div>
                  <div className="text-sm text-dark-400 mt-1">
                    Lots only appear after you sell a previously bought asset. Buys alone don't trigger a taxable event.
                  </div>
                </div>
              </Card>
            )}

            {/* Lot table */}
            {report.lotCount > 0 && (
              <Card className="p-0">
                <div className="flex items-center justify-between px-6 py-4 border-b border-dark-700/50">
                  <div className="text-sm font-semibold">8949 line items</div>
                  <div className="text-xs text-dark-400">{report.lotCount} lots</div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-dark-400 uppercase tracking-wide border-b border-dark-700/50">
                        <th className="text-left px-6 py-3">Asset</th>
                        <th className="text-right px-3 py-3">Qty</th>
                        <th className="text-left px-3 py-3">Acquired</th>
                        <th className="text-left px-3 py-3">Sold</th>
                        <th className="text-right px-3 py-3">Cost basis</th>
                        <th className="text-right px-3 py-3">Proceeds</th>
                        <th className="text-right px-3 py-3">Gain / loss</th>
                        <th className="text-center px-3 py-3">Term</th>
                        <th className="text-right px-6 py-3">Days held</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lotsSorted.map((l, i) => (
                        <tr key={i} className="border-b border-dark-800/60 hover:bg-dark-900/40">
                          <td className="px-6 py-3 font-medium">{l.asset}</td>
                          <td className="text-right px-3 py-3 font-mono text-xs">{l.qty}</td>
                          <td className="px-3 py-3 text-dark-300">{l.acquired}</td>
                          <td className="px-3 py-3 text-dark-300">{l.sold}</td>
                          <td className="text-right px-3 py-3">{fmtUsd(l.costBasis)}</td>
                          <td className="text-right px-3 py-3">{fmtUsd(l.proceeds)}</td>
                          <td className={`text-right px-3 py-3 font-medium ${pnlClass(l.gainLoss)}`}>
                            <span className="inline-flex items-center gap-1 justify-end">
                              {l.gainLoss > 0 ? <TrendingUp className="w-3 h-3" /> :
                               l.gainLoss < 0 ? <TrendingDown className="w-3 h-3" /> : null}
                              {fmtUsd(l.gainLoss)}
                            </span>
                          </td>
                          <td className="text-center px-3 py-3">
                            <span className={`text-[11px] px-2 py-0.5 rounded ${
                              l.term === 'long' ? 'bg-emerald-900/40 text-emerald-300' : 'bg-amber-900/40 text-amber-300'
                            }`}>
                              {l.term === 'long' ? 'LONG' : 'SHORT'}
                            </span>
                          </td>
                          <td className="text-right px-6 py-3 text-dark-300">{l.holdingDays}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}

